import { UseFormWatch } from 'react-hook-form';
import { SchemaNode, DocumentSchema, InputNode, UpdateRule } from '../types';

export const isRequired = (field: SchemaNode): boolean => {
    if (
        (field.kind === 'input' || field.kind === 'action') &&
        field.validation
    ) {
        return field.validation.some((rule) => rule.type === 'required');
    }
    return false;
};

export const getRequiredFields = (schema: DocumentSchema): InputNode[] => {
    const requiredFields: InputNode[] = [];

    const processNode = (node: SchemaNode) => {
        if (node.kind === 'input' && isRequired(node)) {
            requiredFields.push(node);
        }
        if (node.kind === 'section' && node.fields) {
            node.fields.forEach(processNode);
        }
        if (node.kind === 'input' && node.fields) {
            node.fields.forEach(processNode);
        }
    };

    schema.sections.forEach(processNode);
    return requiredFields;
};

export const validateRequiredFields = (
    schema: DocumentSchema,
    data: Record<string, any>,
    checkHidden?: (condition?: string, localContext?: any) => boolean
): { isValid: boolean; missingFields: { id: string; title: string }[] } => {
    const missingFields: { id: string; title: string }[] = [];
    const evaluateCondition = (
        condition?: string,
        localContext?: Record<string, any>
    ): boolean => {
        if (!condition) return true;

        const getValue = (key: string): any => {
            if (key.endsWith('.length')) {
                const baseKey = key.slice(0, -7);
                const val = getValue(baseKey);
                return Array.isArray(val) ? val.length : 0;
            }
            if (localContext && localContext[key] !== undefined) {
                return localContext[key];
            }
            return data?.[key];
        };

        const evaluateExpression = (expr: string): boolean => {
            const trimmed = expr.trim();

            if (trimmed.includes('||')) {
                return trimmed
                    .split('||')
                    .some((part) => evaluateExpression(part));
            }
            if (trimmed.includes('&&')) {
                return trimmed
                    .split('&&')
                    .every((part) => evaluateExpression(part));
            }

            if (trimmed.startsWith('!')) {
                const key = trimmed.slice(1).trim();
                if (
                    !key.includes(' ') &&
                    !key.includes('=') &&
                    !key.includes('>') &&
                    !key.includes('<')
                ) {
                    return !getValue(key);
                }
            }

            if (trimmed.includes('!==')) {
                const [key, val] = trimmed.split('!==').map((s) => s.trim());
                return getValue(key) !== val.replace(/['"]/g, '');
            }

            if (trimmed.includes('===')) {
                const [key, val] = trimmed.split('===').map((s) => s.trim());
                return getValue(key) === val.replace(/['"]/g, '');
            }

            if (trimmed.includes('==')) {
                const [key, val] = trimmed.split('==').map((s) => s.trim());
                // eslint-disable-next-line eqeqeq
                return getValue(key) == val.replace(/['"]/g, '');
            }

            if (trimmed.includes('>')) {
                const [key, val] = trimmed.split('>').map((s) => s.trim());
                return Number(getValue(key)) > Number(val);
            }

            if (trimmed.includes('<')) {
                const [key, val] = trimmed.split('<').map((s) => s.trim());
                return Number(getValue(key)) < Number(val);
            }

            return !!getValue(trimmed);
        };

        return evaluateExpression(condition);
    };

    const isEmptyValue = (value: any) =>
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

    const processNodes = (
        nodes: SchemaNode[],
        localContext?: Record<string, any>
    ) => {
        for (const node of nodes) {
            if (
                node.hidden &&
                checkHidden &&
                checkHidden(node.hidden, localContext)
            ) {
                continue;
            }

            if (node.kind === 'section') {
                processNodes(node.fields || [], localContext);
                continue;
            }

            if (node.kind !== 'input') {
                continue;
            }

            const requiredApplies = (node.validation || []).some(
                (rule) =>
                    rule.type === 'required' &&
                    (!rule.when || evaluateCondition(rule.when, localContext))
            );

            const nodeValue =
                localContext && localContext[node.id] !== undefined
                    ? localContext[node.id]
                    : data[node.id];

            if (requiredApplies && isEmptyValue(nodeValue)) {
                missingFields.push({ id: node.id, title: node.title });
            }

            if (node.inputType === 'repeater' && node.fields?.length) {
                const repeaterItems = Array.isArray(nodeValue) ? nodeValue : [];
                repeaterItems.forEach((item) => {
                    if (item && typeof item === 'object') {
                        processNodes(node.fields || [], item);
                    }
                });
            }
        }
    };

    processNodes(schema.sections as SchemaNode[]);

    const uniqueMissingFields = missingFields.filter(
        (field, index, all) =>
            all.findIndex(
                (candidate) =>
                    candidate.id === field.id && candidate.title === field.title
            ) === index
    );

    return {
        isValid: uniqueMissingFields.length === 0,
        missingFields: uniqueMissingFields,
    };
};

export const createCheckDisable = (
    watch: UseFormWatch<any>,
    contextData?: Record<string, any>
) => {
    const evaluate = createEvaluateCondition(watch, contextData);
    return (condition?: string): boolean => {
        if (!condition) return false;
        return evaluate(condition);
    };
};

export const createEvaluateCondition = (
    watch: UseFormWatch<any>,
    contextData?: Record<string, any>
) => {
    return (condition?: string, localContext?: any): boolean => {
        if (!condition) return true;

        const getValue = (key: string): any => {
            if (key === 'value') return localContext?.value;
            if (key === 'previousValue') return localContext?.previousValue;

            if (key.endsWith('.length')) {
                const baseKey = key.slice(0, -7);
                const val = getValue(baseKey);
                return Array.isArray(val) ? val.length : 0;
            }

            if (localContext && localContext[key] !== undefined) {
                return localContext[key];
            }
            const watchedValue = watch(key);
            if (watchedValue !== undefined) {
                return watchedValue;
            }
            return contextData?.[key];
        };

        const evaluateExpression = (expr: string): boolean => {
            expr = expr.trim();
            if (expr.includes('||')) {
                const parts = expr.split('||');
                return parts.some((p) => evaluateExpression(p));
            }
            if (expr.includes('&&')) {
                const parts = expr.split('&&');
                return parts.every((p) => evaluateExpression(p));
            }

            if (expr.startsWith('!')) {
                const key = expr.slice(1).trim();
                if (
                    !key.includes(' ') &&
                    !key.includes('=') &&
                    !key.includes('>') &&
                    !key.includes('<')
                ) {
                    return !getValue(key);
                }
            }

            // Comparison RHS: quoted strings and true/false are literals;
            // an unquoted identifier resolves as a field/context value with a
            // literal-string fallback (existing schemas only use quoted RHS).
            const resolveOperand = (raw: string): any => {
                const trimmed = raw.trim();
                if (/^['"].*['"]$/.test(trimmed)) {
                    return trimmed.slice(1, -1);
                }
                if (trimmed === 'true') return true;
                if (trimmed === 'false') return false;
                const resolved = getValue(trimmed);
                return resolved !== undefined ? resolved : trimmed;
            };

            if (expr.includes('!==')) {
                const [key, val] = expr.split('!==').map((s) => s.trim());
                return getValue(key) !== resolveOperand(val);
            }

            if (expr.includes('===')) {
                const [key, val] = expr.split('===').map((s) => s.trim());
                return getValue(key) === resolveOperand(val);
            }

            if (expr.includes('==')) {
                const [key, val] = expr.split('==').map((s) => s.trim());
                // eslint-disable-next-line eqeqeq
                return getValue(key) == resolveOperand(val);
            }

            if (expr.includes('>')) {
                const [key, val] = expr.split('>').map((s) => s.trim());
                return Number(getValue(key)) > Number(val);
            }

            if (expr.includes('<')) {
                const [key, val] = expr.split('<').map((s) => s.trim());
                return Number(getValue(key)) < Number(val);
            }

            return !!getValue(expr);
        };

        return evaluateExpression(condition);
    };
};

export const createCheckHidden = (
    watch: UseFormWatch<any>,
    contextData?: Record<string, any>
) => {
    const evaluate = createEvaluateCondition(watch, contextData);
    return (condition?: string, localContext?: any): boolean => {
        if (!condition) return false;
        return evaluate(condition, localContext);
    };
};

export const executeUpdateRules = (
    updates: UpdateRule[] | undefined,
    context: {
        watch: (field: string) => any;
        setValue: (field: string, value: any) => void;
        updateData: (field: string, value: any) => void;
        evaluateCondition: (condition?: string, localContext?: any) => boolean;
        localContext?: Record<string, any>;
    }
) => {
    if (!updates) return;

    updates.forEach((update) => {
        if (
            update.condition &&
            !context.evaluateCondition(update.condition, context.localContext)
        ) {
            return;
        }

        let val = update.value;
        if (update.source && update.value === undefined) {
            val = context.watch(update.source);
        }

        if (val === '${today}') {
            val = new Date().toISOString().split('T')[0];
        }

        const currentTargetValue = context.watch(update.target);
        if (update.operation === 'push') {
            const arr = Array.isArray(currentTargetValue)
                ? [...currentTargetValue]
                : [];
            if (update.uniqueBy && val && typeof val === 'object') {
                const exists = arr.some(
                    (item: any) =>
                        item?.[update.uniqueBy!] === val?.[update.uniqueBy!]
                );
                if (exists) return;
            }
            let valueToAdd = typeof val === 'object' ? { ...val } : val;
            if (valueToAdd?.id === '${uuid}') {
                valueToAdd.id = Date.now().toString();
            }
            if (valueToAdd?.sourceDate === '${today}') {
                valueToAdd.sourceDate = new Date().toISOString().split('T')[0];
            }
            arr.push(valueToAdd);
            context.updateData(update.target, arr);
            context.setValue(update.target, arr);
            return;
        }

        context.updateData(update.target, val);
        context.setValue(update.target, val);
    });
};
